import { useEffect, useMemo, useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { useGetApplicationsQuery } from '@features/apps';
import {
  useGetProjectUsersQuery,
  useRemoveUserFromProjectMutation,
  useUpdateProjectUserAdminMutation,
} from '@features/users';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderIcon from '@mui/icons-material/Folder';
import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate, useOutletContext, useParams } from 'react-router';

import { ConfirmDialog, InlineLoader, PageLoader } from '@/components/common';
import { logger } from '@/utils/logger';

import { InviteProjectUsersModal } from './InviteProjectUsersModal';

interface TenantContextType {
  tenantId: string;
  tenant: {
    tenantId: string;
    name: string;
  };
}

/**
 * Project Users Tab
 * Shows list of projects (apps) for current tenant
 * When a project is selected, displays users with permissions for that project
 */
export const ProjectUsers = () => {
  const { tenantId } = useOutletContext<TenantContextType>();
  const { orgId, projectId } = useParams<{ orgId: string; projectId?: string }>();
  const navigate = useNavigate();
  const { user: auth0User } = useAuth0();
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [updatingUserId, setUpdatingUserId] = useState<number | null>(null);
  const [removingUserId, setRemovingUserId] = useState<number | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<{ id: number; username: string } | null>(null);

  // Get all applications for current tenant
  const { data: allApps = [] } = useGetApplicationsQuery();
  const tenantApps = allApps
    .filter((app) => String(app.tenantId) === String(tenantId))
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

  // Auto-select first project if no projectId in URL
  useEffect(() => {
    if (!projectId && tenantApps.length > 0) {
      navigate(
        `/account-settings/organizations/${orgId}/projects/${String(tenantApps[0].id)}/users`,
        {
          replace: true,
        }
      );
    }
  }, [projectId, tenantApps, orgId, navigate]);

  // Use projectId from URL as selected app
  const selectedAppId = projectId || null;

  // Get project users for selected app
  const {
    data: projectUsers = [],
    isLoading: usersLoading,
    error: usersError,
  } = useGetProjectUsersQuery(
    { tenantId, appId: selectedAppId! },
    {
      skip: !selectedAppId,
      refetchOnMountOrArgChange: true,
    }
  );

  // Mutations
  const [updateUserAdmin] = useUpdateProjectUserAdminMutation();
  const [removeUser] = useRemoveUserFromProjectMutation();

  // Sort users alphabetically
  // Note: Users without valid Identity are already filtered out by the mapper (users.mappers.ts)
  const sortedUsers = useMemo(() => {
    return [...projectUsers].sort((a, b) => {
      const usernameA = a.username.toLowerCase();
      const usernameB = b.username.toLowerCase();
      return usernameA.localeCompare(usernameB);
    });
  }, [projectUsers]);

  // Current user permissions
  const currentUserEmail = auth0User?.email;
  const currentUserInProject = projectUsers.find((u) => u.username === currentUserEmail);
  const currentUserId = currentUserInProject?.id;
  const isAdmin = currentUserInProject?.admin ?? false;

  const handleProjectClick = (appId: string) => {
    navigate(`/account-settings/organizations/${orgId}/projects/${appId}/users`);
  };

  const handleToggleAdmin = async (userId: number, currentAdminState: boolean) => {
    if (!selectedAppId) return;
    setUpdatingUserId(userId);
    try {
      await updateUserAdmin({
        tenantId,
        appId: selectedAppId,
        userId,
        isAdmin: !currentAdminState,
      }).unwrap();
    } catch (err) {
      logger.error('Failed to update user admin status:', err);
      alert('Failed to update user role');
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleRemoveUser = async (userId: number) => {
    if (!selectedAppId) return;

    setRemovingUserId(userId);
    try {
      await removeUser({ tenantId, appId: selectedAppId, userId }).unwrap();
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (err) {
      logger.error('Failed to remove user:', err);
      alert('Failed to remove user');
    } finally {
      setRemovingUserId(null);
    }
  };

  const openDeleteDialog = (user: { id: number; username: string }) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const getRoleTooltip = (userId: number): string => {
    if (!isAdmin) return "You don't have admin access!";
    if (userId === currentUserId) return 'Cannot change your own role';
    return 'Toggle admin role';
  };

  const getRemoveTooltip = (userId: number): string => {
    if (userId === currentUserId) return 'Cannot remove yourself';
    return 'Remove user from project';
  };

  const renderUsersTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Members</TableCell>
            <TableCell>Role</TableCell>
            {isAdmin && <TableCell align="center">Action</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedUsers.map((user) => (
            <TableRow key={user.id}>
              <TableCell>{user.username}</TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ minWidth: 50 }}>
                    {user.admin ? 'Admin' : 'User'}
                  </Typography>
                  <Tooltip title={getRoleTooltip(user.id)}>
                    <span>
                      <Switch
                        checked={user.admin}
                        onChange={() => handleToggleAdmin(user.id, user.admin)}
                        disabled={
                          user.id === currentUserId || !isAdmin || updatingUserId === user.id
                        }
                        size="small"
                      />
                    </span>
                  </Tooltip>
                  {updatingUserId === user.id && <InlineLoader size={16} />}
                </Box>
              </TableCell>
              {isAdmin && (
                <TableCell align="center">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                    }}
                  >
                    <Tooltip title={getRemoveTooltip(user.id)}>
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => openDeleteDialog({ id: user.id, username: user.username })}
                          disabled={user.id === currentUserId || removingUserId === user.id}
                        >
                          <DeleteIcon
                            fontSize="small"
                            color={
                              user.id === currentUserId || removingUserId === user.id
                                ? 'disabled'
                                : 'error'
                            }
                          />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {removingUserId === user.id && <InlineLoader size={16} />}
                  </Box>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderUsersSection = () => {
    if (!selectedAppId) {
      return <Alert severity="info">Select a project to view and manage users</Alert>;
    }
    if (usersLoading) {
      return <PageLoader variant="section" />;
    }
    if (usersError) {
      return <Alert severity="error">Failed to load project users</Alert>;
    }
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">Project Users</Typography>
          {isAdmin && (
            <Button variant="contained" onClick={() => setInviteModalOpen(true)}>
              Invite Users
            </Button>
          )}
        </Box>

        {sortedUsers.length === 0 ? (
          <Alert severity="info">No users found for this project</Alert>
        ) : (
          renderUsersTable()
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', gap: 3, height: '70vh' }}>
      {/* Left: Project List */}
      <Paper sx={{ width: 320, p: 2, overflowY: 'auto' }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Projects
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {tenantApps.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No projects found
          </Typography>
        ) : (
          <List sx={{ py: 0 }}>
            {tenantApps.map((app, index) => (
              <Box key={app.id}>
                <ListItemButton
                  selected={selectedAppId === app.id}
                  onClick={() => handleProjectClick(String(app.id))}
                  sx={{
                    py: 1.5,
                    px: 1,
                    bgcolor: selectedAppId === app.id ? 'primary.50' : 'transparent',
                    '&.Mui-selected': {
                      bgcolor: 'primary.50',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <FolderIcon
                      sx={{ color: selectedAppId === app.id ? 'primary.main' : 'action.active' }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {app.name}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {app.description || 'No description.'}
                      </Typography>
                    }
                  />
                </ListItemButton>
                {index < tenantApps.length - 1 && <Divider />}
              </Box>
            ))}
          </List>
        )}
      </Paper>

      {/* Right: Users Table */}
      <Box sx={{ flex: 1 }}>{renderUsersSection()}</Box>

      {/* Invite Modal */}
      {selectedAppId && (
        <InviteProjectUsersModal
          isOpen={inviteModalOpen}
          onClose={() => setInviteModalOpen(false)}
          tenantId={tenantId}
          appId={selectedAppId}
          identityIsAdmin={isAdmin}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={() => userToDelete && handleRemoveUser(userToDelete.id)}
        title="Remove User from Project"
        message={
          <>
            Are you sure you want to remove <strong>{userToDelete?.username}</strong> from this
            project? They will lose access to all project resources.
          </>
        }
        confirmText="Remove User"
        confirmColor="error"
        isLoading={removingUserId !== null}
        loadingText="Removing..."
      />
    </Box>
  );
};
